<template>
    <slot />
</template>

<script setup>
    import { createMotionVectors } from '#imports';
    import gsap from 'gsap';
    import { Vector3 } from 'three';
    import { onMounted, ref, watchEffect } from 'vue';

    import { useRafBool } from '@resn/gozer-vue';

    import { useBaseball } from '~/components/gl/common/Objects/useBaseball';

    const props = defineProps({
        object: { type: Object, default: null, required: true },
    });

    const active = ref(false);

    const ambientMotion = createMotionVectors();
    const transitionMotion = createMotionVectors();
    const finalRotation = new Vector3();

    const object = props.object;
    object.scale.setScalar(1.5);

    const { material } = useBaseball({ object });

    onMounted(() => {
        material.uOverlayStr = 0.66;
        material.uOverlayColor.set('#78412a');
        material.uFresnelStr = 0.0;
    });

    const show = ({ inDelay = 0, outDelay = 2.5 } = {}) => {
        const tl = new gsap.timeline({
            delay: inDelay,
            onStart: () => (active.value = true),
            onComplete: () => (active.value = false),
        });

        tl.fromTo(
            material.envMapRotation,
            { y: -Math.PI },
            { y: 0, duration: 1.2, ease: 'sine.inOut' },
            0
        );
        tl.fromTo(material.uniforms.uAlpha, { value: 0 }, { value: 1, duration: 0.8 }, 0);
        tl.fromTo(
            transitionMotion.rot,
            { x: 0 },
            { x: 4, duration: 2, ease: 'power1.inOut' },
            outDelay
        );
        tl.fromTo(
            transitionMotion.pos,
            { x: 0, y: 0, z: 0 },
            { x: 0.1, y: -1, z: 5, duration: 2, ease: 'power1.inOut' },
            outDelay + 0.5
        );

        return tl;
    };

    watchEffect(() => {
        object.visible = active.value;
    });

    useRafBool(active, ({ delta }) => {
        ambientMotion.rot.y += delta * 0.001;

        finalRotation.copy(transitionMotion.rot).add(ambientMotion.rot);

        object.position.copy(transitionMotion.pos).add(ambientMotion.pos);
        object.rotation.setFromVector3(finalRotation);
    });

    defineExpose({ show, id: 'baseball' });
</script>

<style lang="scss" scoped></style>
